import { z } from 'zod'
import { optionalText, pageQuerySchema, versionSchema } from './common.js'
import { type TaskTeamRef, type TaskUserRef, type TeamRole, teamRoleSchema } from './tasks.js'

/**
 * Teams (ADR-023): people grouped with LEAD / MEMBER roles, a company capability set up in Settings
 * (`teams.manage`). Tasks use them for who owns work, Service for who makes visits.
 */

export const teamMemberInputSchema = z.object({ userId: z.uuid(), role: teamRoleSchema.default('MEMBER') })

export const teamInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  nameAr: optionalText(100),
  description: optionalText(1_000),
  allowSelfClaim: z.boolean().default(true),
  /** Makes service visits: Service's board and visit pickers list only these teams. */
  fieldService: z.boolean().default(false),
  members: z
    .array(teamMemberInputSchema)
    .max(200)
    .default([])
    .refine((m) => new Set(m.map((x) => x.userId)).size === m.length, 'A person is listed twice'),
})
export type TeamInput = z.input<typeof teamInputSchema>

/** Without `fieldService`, the team keeps its mark. */
export const teamUpdateSchema = teamInputSchema.extend({
  version: versionSchema,
  fieldService: z.boolean().optional(),
})
export type TeamUpdate = z.input<typeof teamUpdateSchema>

export const teamListQuerySchema = pageQuerySchema.extend({
  view: z.enum(['active', 'archived', 'mine']).default('active'),
})
export type TeamListQuery = z.input<typeof teamListQuerySchema>

/** The team's open tasks the viewer can see (zeros for a viewer without tasks, e.g. the module off). */
export interface TeamTaskCounts {
  open: number
  unassigned: number
  overdue: number
  blocked: number
  inReview: number
}

export interface TeamMember {
  user: TaskUserRef
  role: TeamRole
}

export interface Team extends TaskTeamRef {
  description: string | null
  allowSelfClaim: boolean
  /** Optional for older fixtures. */
  fieldService?: boolean
  archivedAt: string | null
  /** Leads first, then by name. */
  members: TeamMember[]
  counts: TeamTaskCounts
  myRole: TeamRole | null
  canManage: boolean
  version: number
}

export const TEAM_ERROR_CODES = ['TEAM_ARCHIVED', 'TEAM_HAS_OPEN_TASKS', 'TEAM_HAS_OPEN_VISITS'] as const
export type TeamErrorCode = (typeof TEAM_ERROR_CODES)[number]
